import { useEffect, useRef, useState } from "react";
import { Loader2, Save, UploadCloud, Eye, RotateCcw, Rocket, ChevronUp, ChevronDown, Image as ImageIcon, Layout as LayoutIcon, Palette, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import ImageCropModal from "@/components/admin/ImageCropModal";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30";

export default function AdminSiteCmsPage() {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState({ has_unpublished_changes: false, is_published: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState("");
  const [crop, setCrop] = useState(null); // { field, src, aspect }
  const logoInput = useRef(null);
  const heroInput = useRef(null);

  const load = () =>
    apiClient.get("/admin/site-cms").then((r) => {
      setCfg(r.data.draft);
      setStatus({ has_unpublished_changes: r.data.has_unpublished_changes, is_published: r.data.is_published });
    });

  useEffect(() => { load().catch(() => {}).finally(() => setLoading(false)); }, []);

  const setBranding = (k, v) => setCfg((c) => ({ ...c, branding: { ...c.branding, [k]: v } }));
  const setHero = (k, v) => setCfg((c) => ({ ...c, hero: { ...c.hero, [k]: v } }));
  const setPage = (pageKey, field, v) => setCfg((c) => ({
    ...c, pages: { ...(c.pages || {}), [pageKey]: { ...((c.pages || {})[pageKey] || {}), [field]: v } },
  }));
  const setPagesField = (k, v) => setCfg((c) => ({ ...c, pages: { ...(c.pages || {}), [k]: v } }));
  const setSection = (i, k, v) => setCfg((c) => {
    const s = [...c.sections]; s[i] = { ...s[i], [k]: v }; return { ...c, sections: s };
  });
  const setWhyCard = (i, k, v) => setCfg((c) => {
    const cards = [...(c.why_avasa_cards || [])]; cards[i] = { ...cards[i], [k]: v };
    return { ...c, why_avasa_cards: cards };
  });
  const move = (i, dir) => setCfg((c) => {
    const s = [...c.sections]; const j = i + dir;
    if (j < 0 || j >= s.length) return c;
    [s[i], s[j]] = [s[j], s[i]];
    s.forEach((x, idx) => (x.order = idx));
    return { ...c, sections: s };
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put("/admin/site-cms", cfg);
      setStatus({ has_unpublished_changes: data.has_unpublished_changes, is_published: data.is_published });
      toast.success("Draft tersimpan.");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await apiClient.put("/admin/site-cms", cfg);
      const { data } = await apiClient.post("/admin/site-cms/publish");
      setStatus({ has_unpublished_changes: data.has_unpublished_changes, is_published: data.is_published });
      toast.success("Beranda AVASA berhasil dipublish.");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setPublishing(false); }
  };

  const reset = async () => {
    if (!window.confirm("Kembalikan draft ke pengaturan default? Perubahan draft yang belum dipublish akan hilang.")) return;
    try { const { data } = await apiClient.post("/admin/site-cms/reset"); setCfg(data.draft); setStatus({ has_unpublished_changes: data.has_unpublished_changes, is_published: data.is_published }); toast.success("Draft dikembalikan ke default."); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const upload = async (field, file, ref) => {
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await apiClient.post(`/admin/site-cms/image/${field}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (field === "logo") setBranding("logo_url", data.url); else setHero("bg_image", data.url);
      toast.success("Gambar diunggah. Jangan lupa Simpan & Publish.");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setUploading(""); if (ref?.current) ref.current.value = ""; }
  };

  const openCrop = (field, file, ref) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCrop({ field, src: reader.result, aspect: field === "logo" ? 3 : 16 / 9 });
      if (ref?.current) ref.current.value = "";
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = async (croppedFile) => {
    const field = crop?.field;
    if (!field) return;
    await upload(field, croppedFile);
    setCrop(null);
  };

  if (loading || !cfg) {
    return <AdminLayout testid="admin-site-cms-page"><div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-primary" size={28} /></div></AdminLayout>;
  }

  return (
    <AdminLayout testid="admin-site-cms-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">CMS — Beranda AVASA</h1>
          <p className="text-sm text-muted-foreground mt-1">Atur tampilan halaman utama publik AVASA tanpa mengubah kode.</p>
        </div>
        <div className="flex items-center gap-2">
          {status.has_unpublished_changes && <span className="text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="cms-status">Ada perubahan belum dipublish</span>}
          <a href="/" target="_blank" rel="noreferrer" data-testid="cms-preview" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm font-semibold hover:bg-secondary/60"><Eye size={15} /> Preview Publik</a>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Branding */}
        <Card title="Identitas & Branding" icon={Palette} testid="cms-branding">
          <div className="grid grid-cols-2 gap-4">
            <UploadBox label="Logo" url={cfg.branding.logo_url} busy={uploading === "logo"} aspect="aspect-video"
              onPick={() => logoInput.current?.click()} onRemove={() => setBranding("logo_url", "")} testid="cms-logo" />
            <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={(e) => openCrop("logo", e.target.files?.[0], logoInput)} />
            <div className="grid grid-cols-2 gap-2">
              <ColorField label="Warna Utama" value={cfg.branding.primary_color} onChange={(v) => setBranding("primary_color", v)} testid="cms-primary-color" />
              <ColorField label="Warna Aksen" value={cfg.branding.accent_color} onChange={(v) => setBranding("accent_color", v)} testid="cms-accent-color" />
            </div>
          </div>
          <Field label="Nama Brand"><input className={inputCls} value={cfg.branding.brand_name || ""} onChange={(e) => setBranding("brand_name", e.target.value)} data-testid="cms-brand-name" /></Field>
          <Field label="Tagline"><input className={inputCls} value={cfg.branding.tagline || ""} onChange={(e) => setBranding("tagline", e.target.value)} data-testid="cms-tagline" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email Kontak"><input className={inputCls} value={cfg.branding.contact_email || ""} onChange={(e) => setBranding("contact_email", e.target.value)} data-testid="cms-email" /></Field>
            <Field label="WhatsApp"><input className={inputCls} value={cfg.branding.contact_whatsapp || ""} onChange={(e) => setBranding("contact_whatsapp", e.target.value)} data-testid="cms-whatsapp" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="No. HP / Telepon"><input className={inputCls} value={cfg.branding.contact_phone || ""} onChange={(e) => setBranding("contact_phone", e.target.value)} data-testid="cms-phone" /></Field>
            <Field label="Alamat"><input className={inputCls} value={cfg.branding.contact_address || ""} onChange={(e) => setBranding("contact_address", e.target.value)} data-testid="cms-address" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Instagram"><input className={inputCls} value={cfg.branding.instagram || ""} onChange={(e) => setBranding("instagram", e.target.value)} data-testid="cms-instagram" /></Field>
            <Field label="Facebook"><input className={inputCls} value={cfg.branding.facebook || ""} onChange={(e) => setBranding("facebook", e.target.value)} data-testid="cms-facebook" /></Field>
          </div>
          <Field label="Teks Footer"><textarea rows={2} className={`${inputCls} resize-none`} value={cfg.branding.footer_text || ""} onChange={(e) => setBranding("footer_text", e.target.value)} data-testid="cms-footer" /></Field>
        </Card>

        {/* Hero */}
        <Card title="Hero / Banner Beranda" icon={ImageIcon} testid="cms-hero">
          <Toggle label="Tampilkan Hero" checked={cfg.hero.enabled} onChange={(v) => setHero("enabled", v)} testid="cms-hero-enabled" />
          <UploadBox label="Gambar Background" url={cfg.hero.bg_image} busy={uploading === "hero"} aspect="aspect-video"
            onPick={() => heroInput.current?.click()} onRemove={() => setHero("bg_image", "")} testid="cms-hero-image" />
          <input ref={heroInput} type="file" accept="image/*" className="hidden" onChange={(e) => openCrop("hero", e.target.files?.[0], heroInput)} />
          <Field label="Judul Utama"><input className={inputCls} value={cfg.hero.title || ""} onChange={(e) => setHero("title", e.target.value)} data-testid="cms-hero-title" /><p className="text-[11px] text-muted-foreground mt-1">Tip: bungkus kata dengan tanda bintang, mis. <span className="font-mono">*hunian*</span>, agar kata itu berwarna aksen.</p></Field>
          <Field label="Subjudul"><textarea rows={2} className={`${inputCls} resize-none`} value={cfg.hero.subtitle || ""} onChange={(e) => setHero("subtitle", e.target.value)} data-testid="cms-hero-subtitle" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Teks Tombol CTA"><input className={inputCls} value={cfg.hero.cta_text || ""} onChange={(e) => setHero("cta_text", e.target.value)} data-testid="cms-hero-cta-text" /></Field>
            <Field label="Link CTA"><input className={inputCls} value={cfg.hero.cta_url || ""} onChange={(e) => setHero("cta_url", e.target.value)} data-testid="cms-hero-cta-url" /></Field>
          </div>
          <Field label="Alignment Teks">
            <select className={inputCls} value={cfg.hero.alignment} onChange={(e) => setHero("alignment", e.target.value)} data-testid="cms-hero-align">
              <option value="left">Kiri</option><option value="center">Tengah</option><option value="right">Kanan</option>
            </select>
          </Field>
        </Card>

        {/* Sections */}
        <Card title="Section Beranda" icon={LayoutIcon} testid="cms-sections" className="lg:col-span-2">
          <p className="text-xs text-muted-foreground -mt-2 mb-2">Aktif/nonaktifkan, ubah judul, dan atur urutan section.</p>
          <div className="space-y-3">
            {cfg.sections.map((s, i) => (
              <div key={s.key} className="rounded-xl border border-border p-4" data-testid={`cms-section-${s.key}`}>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <span className="font-heading font-semibold capitalize">{s.key.replace("_", " ")}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded-lg border border-border disabled:opacity-40 hover:bg-secondary/60" data-testid={`cms-section-up-${s.key}`}><ChevronUp size={15} /></button>
                    <button onClick={() => move(i, 1)} disabled={i === cfg.sections.length - 1} className="p-1.5 rounded-lg border border-border disabled:opacity-40 hover:bg-secondary/60" data-testid={`cms-section-down-${s.key}`}><ChevronDown size={15} /></button>
                    <Toggle checked={s.enabled} onChange={(v) => setSection(i, "enabled", v)} testid={`cms-section-toggle-${s.key}`} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input className={inputCls} placeholder="Judul section" value={s.title || ""} onChange={(e) => setSection(i, "title", e.target.value)} data-testid={`cms-section-title-${s.key}`} />
                  <input className={inputCls} placeholder="Subjudul (opsional)" value={s.subtitle || ""} onChange={(e) => setSection(i, "subtitle", e.target.value)} data-testid={`cms-section-subtitle-${s.key}`} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Why AVASA cards */}
        <Card title="Kartu 'Kenapa AVASA'" icon={LayoutIcon} testid="cms-why-cards" className="lg:col-span-2">
          <p className="text-xs text-muted-foreground -mt-2 mb-2">
            Edit judul, deskripsi singkat, dan isi halaman detail tiap kartu. Saat kartu diklik di beranda, pengunjung
            dibawa ke halaman detail berisi teks di bawah ini. Format teks: baris kosong memisahkan paragraf, awali baris
            dengan <span className="font-mono">- </span> untuk poin/bullet.
          </p>
          <div className="grid lg:grid-cols-2 gap-4">
            {(cfg.why_avasa_cards || []).map((card, i) => (
              <div key={card.key} className="rounded-xl border border-border p-4 space-y-3" data-testid={`cms-why-card-${card.key}`}>
                <div className="flex items-center justify-between">
                  <span className="font-heading font-semibold capitalize">{card.key}</span>
                  <a href={`/kenapa-avasa/${card.key}`} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid={`cms-why-preview-${card.key}`}><Eye size={13} /> Lihat</a>
                </div>
                <input className={inputCls} placeholder="Judul kartu" value={card.title || ""} onChange={(e) => setWhyCard(i, "title", e.target.value)} data-testid={`cms-why-title-${card.key}`} />
                <input className={inputCls} placeholder="Deskripsi singkat (tampil di kartu)" value={card.desc || ""} onChange={(e) => setWhyCard(i, "desc", e.target.value)} data-testid={`cms-why-desc-${card.key}`} />
                <input className={inputCls} placeholder="Judul halaman detail" value={card.detail_title || ""} onChange={(e) => setWhyCard(i, "detail_title", e.target.value)} data-testid={`cms-why-detail-title-${card.key}`} />
                <textarea rows={8} className={`${inputCls} resize-y leading-relaxed`} placeholder="Isi halaman detail..." value={card.detail_content || ""} onChange={(e) => setWhyCard(i, "detail_content", e.target.value)} data-testid={`cms-why-detail-content-${card.key}`} />
              </div>
            ))}
          </div>
        </Card>

        {/* Info & Legal pages */}
        <Card title="Halaman Informasi & Legal" icon={FileText} testid="cms-pages" className="lg:col-span-2">
          <p className="text-xs text-muted-foreground -mt-2 mb-2">
            Edit isi halaman publik: Tentang Kami, Syarat &amp; Ketentuan, dan Kebijakan Privasi. Ketik biasa —
            baris kosong memisahkan paragraf, awali baris dengan <span className="font-mono">- </span> untuk poin/bullet.
          </p>
          <p className="text-xs text-muted-foreground -mt-2 mb-2">
            Edit isi halaman publik: Tentang Kami, Syarat &amp; Ketentuan, dan Kebijakan Privasi. Ketik biasa —
            baris kosong memisahkan paragraf, awali baris dengan <span className="font-mono">- </span> untuk poin/bullet.
          </p>
          <Field label="Kalimat Pembuka Halaman 'Hubungi Kami'">
            <textarea rows={2} className={`${inputCls} resize-none`} value={(cfg.pages?.contact_intro) || ""} onChange={(e) => setPagesField("contact_intro", e.target.value)} data-testid="cms-contact-intro" />
          </Field>
          <div className="grid lg:grid-cols-3 gap-4">
            {[
              { key: "about", label: "Tentang Kami", slug: "/tentang-kami" },
              { key: "terms", label: "Syarat & Ketentuan", slug: "/syarat-ketentuan" },
              { key: "privacy", label: "Kebijakan Privasi", slug: "/kebijakan-privasi" },
            ].map((p) => (
              <div key={p.key} className="rounded-xl border border-border p-4 space-y-3" data-testid={`cms-page-${p.key}`}>
                <div className="flex items-center justify-between">
                  <span className="font-heading font-semibold">{p.label}</span>
                  <a href={p.slug} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid={`cms-page-preview-${p.key}`}><Eye size={13} /> Lihat</a>
                </div>
                <input className={inputCls} placeholder="Judul halaman" value={(cfg.pages?.[p.key]?.title) || ""} onChange={(e) => setPage(p.key, "title", e.target.value)} data-testid={`cms-page-title-${p.key}`} />
                <textarea rows={12} className={`${inputCls} resize-y leading-relaxed`} placeholder="Isi konten halaman..." value={(cfg.pages?.[p.key]?.content) || ""} onChange={(e) => setPage(p.key, "content", e.target.value)} data-testid={`cms-page-content-${p.key}`} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Action bar */}
      <div className="sticky bottom-0 mt-6 -mx-6 px-6 py-4 bg-background/90 backdrop-blur border-t border-border flex flex-wrap gap-3 justify-end">
        <button onClick={reset} data-testid="cms-reset" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border font-semibold hover:bg-secondary/60"><RotateCcw size={16} /> Reset Default</button>
        <button onClick={save} disabled={saving} data-testid="cms-save" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-primary/40 text-primary font-semibold hover:bg-primary/5 disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Simpan Draft</button>
        <button onClick={publish} disabled={publishing} data-testid="cms-publish" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60">{publishing ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />} Publish</button>
      </div>

      <ImageCropModal
        key={crop?.field || "closed"}
        open={!!crop}
        imageSrc={crop?.src}
        defaultAspect={crop?.aspect || 16 / 9}
        filename={crop?.field === "logo" ? "logo.png" : "hero.png"}
        busy={!!uploading}
        onCancel={() => setCrop(null)}
        onConfirm={handleCropConfirm}
      />
    </AdminLayout>
  );
}

function Card({ title, icon: Icon, children, testid, className = "" }) {
  return (
    <div className={`bg-card border border-border rounded-2xl p-6 space-y-4 ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2"><Icon size={18} className="text-primary" /><h2 className="font-heading text-lg font-semibold">{title}</h2></div>
      {children}
    </div>
  );
}
function Field({ label, children }) { return (<div><label className="block text-xs text-muted-foreground mb-1">{label}</label>{children}</div>); }
function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs text-muted-foreground mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#2f5d50"} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-10 h-10 rounded-lg border border-border bg-background cursor-pointer" />
        <input className={inputCls} value={value || ""} placeholder="#hex" onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
}
function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      {label && <span className="text-sm">{label}</span>}
      <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
        className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-primary" : "bg-muted"}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
      </button>
    </label>
  );
}
function UploadBox({ label, url, busy, aspect, onPick, onRemove, testid }) {
  return (
    <div>
      <label className="block text-xs text-muted-foreground mb-1">{label}</label>
      <div className="relative">
        <button type="button" onClick={onPick} data-testid={`${testid}-upload`}
          className={`relative w-full ${aspect} rounded-xl border border-dashed border-border bg-secondary/30 overflow-hidden grid place-items-center hover:border-primary/50 transition-colors`}>
          {url ? <img src={url} alt={label} className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
            : <span className="flex flex-col items-center gap-1 text-muted-foreground text-xs"><UploadCloud size={18} /> Unggah</span>}
          {busy && <span className="absolute inset-0 grid place-items-center bg-black/30"><Loader2 size={20} className="animate-spin text-white" /></span>}
        </button>
        {url && onRemove && !busy && (
          <button type="button" onClick={onRemove} data-testid={`${testid}-remove`}
            className="absolute top-1.5 right-1.5 grid place-items-center w-7 h-7 rounded-full bg-black/60 text-white hover:bg-destructive transition-colors"
            title="Hapus gambar (kembali ke default)">
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
